// ==== Custom functions for Portfolio =======================================================
import { select, event } from "d3";
import {
  bigRectCreate,
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import { indexOfSmallest } from "@/_core/_helpers/_helpers";
import { simulationsTooltip } from "@/_core/charts/D3ChartOptimizerHelpers";
import {
  colorDarkExpo,
  colorExpo,
  colorHedge,
  dnCandles,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";

export const D3HoverSimulations = function() {};

D3HoverSimulations.prototype.hoverFct = function(
  svg,
  data,
  customName,
  formatNumber,
  currency,
  showTooltip,
  axisFormatNumber = null,
  colorTheme,
  baseCurrency,
) {
  if (axisFormatNumber === null) {
    axisFormatNumber = formatNumber;
  }
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("width", 250 + "px")
    .html("");

  let Tooltip2b;
  if (!Number.isNaN(data[0].result2)) {
    Tooltip2b = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", "tooltip")
      .style("color", textactive2(colorTheme))
      .style("background", dnCandles(colorTheme))
      .style("border", dnCandles(colorTheme))
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .html("");
  }
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("color", textactive2(colorTheme))
    .style("background", colorHedge(colorTheme))
    .style("border", colorHedge(colorTheme))
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  let tooltipWidth2 = 60;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function() {
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(event.layerX - svg.margin.left, svg.width),
      0,
    );
    let xRate = svg.x.invert(xPixels);
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    let yRate = svg.y.invert(yPixels);
    let idx = indexOfSmallest(
      data.map(
        (d) => 3 * Math.pow(d.rates - xRate, 2) + Math.pow(d.result - yRate, 2),
      ),
    );
    let x = data[idx].rates;
    let y = data[idx].result;
    let y2 = data[idx].result2;

    select(".hHoverLineSIMS")
      .style("opacity", 1)
      .attr("y1", svg.y(y))
      .attr("y2", svg.y(y));
    select(".vHoverLineSIMS")
      .style("opacity", 1)
      .attr("x1", svg.x(x))
      .attr("x2", svg.x(x));
    select(".dotSIMS")
      .style("opacity", 1)
      .attr("transform", `translate(${svg.x(x)},${svg.y(y)})`);
    select(".dotSIMSe")
      .style("opacity", 1)
      .attr("transform", `translate(${svg.x(x)},${svg.y(x)})`);
    if (!Number.isNaN(data[0].result2)) {
      select(".dotSIMSb")
        .style("opacity", 1)
        .attr("transform", `translate(${svg.x(x)},${svg.y(y2)})`);
    }

    if (showTooltip) {
      let txt = simulationsTooltip(
        data[idx],
        currency,
        formatNumber,
        baseCurrency,
      );

      Tooltip.style("opacity", 1)
        .style("left", 19 + svg.margin.left + "px")
        .style("top", 60 + svg.margin.top + "px")
        .html(txt);
    }

    Tooltip2.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", svg.y(y) + svg.margin.top - 11 + "px")
      .html(axisFormatNumber(y));

    if (!Number.isNaN(data[0].result2)) {
      Tooltip2b.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", svg.y(y2) + svg.margin.top - 11 + "px")
        .html(axisFormatNumber(y2));
    }

    Tooltip3.style("opacity", 1)
      .style("left", svg.x(x) + svg.margin.left - tooltipWidth2 / 2 + "px")
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(formatNumber(x));
  };

  var mouseLeave = function() {
    select(".hHoverLineSIMS")
      .style("opacity", 0)
      .attr("display", null);
    select(".vHoverLineSIMS")
      .style("opacity", 0)
      .attr("display", null);

    if (showTooltip) {
      Tooltip.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
    }

    Tooltip2.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    if (!Number.isNaN(data[0].result2)) {
      Tooltip2b.style("right", 0)
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
    }

    Tooltip3.style("opacity", 0)
      .style("left", "0px")
      .html("");

    select(".dotSIMS")
      .style("opacity", 0)
      .attr("display", null);

    select(".dotSIMSe")
      .style("opacity", 0)
      .attr("display", null);

    if (!Number.isNaN(data[0].result2)) {
      select(".dotSIMSb")
        .style("opacity", 0)
        .attr("display", null);
    }
  };

  hLineCreate(svg, customName, "gray", 1);
  vLineCreate(svg, customName, "gray", 1);
  if (!Number.isNaN(data[0].result2)) {
    dotCreate(svg, customName + "b", 3.5, dnCandles(colorTheme), "white");
  }
  dotCreate(svg, customName + "e", 3.5, colorExpo(colorTheme), "white");
  dotCreate(svg, customName, 5, colorHedge(colorTheme), "white");
  bigRectCreate(data, svg, customName, mouseOver, mouseLeave);
};
